import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { ListaBlancaDialog } from './ListaBlancaDialog';

const ESTADO = {
  ACTIVA: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  VENCIDA: 'bg-amber-50 text-amber-700 border-amber-200',
};
const fechaAR = (v) => (v ? new Date(String(v).length === 10 ? `${v}T12:00:00` : v).toLocaleDateString('es-AR') : '—');
const alcanceTexto = (e) => (!e.alcance || e.alcance === 'TODOS' ? 'Todos los monitores' : (e.alcance_nombres || e.alcance).join(', '));

const Resumen = ({ data }) => {
  if (!data.excluido) return <span className="text-sm text-slate-500" data-testid="lb-resumen">No está excluido de ningún monitor</span>;
  const texto = data.excluido_de_todos ? 'Excluido de todos los monitores' : `Excluido de: ${data.monitores_excluidos.join(', ')}`;
  return <span className="text-sm font-medium text-emerald-700" data-testid="lb-resumen">{texto}</span>;
};

const Fila = ({ e, onEditar, onQuitar }) => {
  const [confirmar, setConfirmar] = useState(false);
  const nueva = e.alcance !== undefined && e.alcance !== null && e.cuit;
  return (
    <tr className="border-t border-slate-100" data-testid={`lb-fila-${e.id}`}>
      <td className="px-3 py-2 text-xs text-slate-700 max-w-[220px]">{alcanceTexto(e)}</td>
      <td className="px-3 py-2 text-sm text-slate-700">{e.motivo}</td>
      <td className="px-3 py-2 text-sm text-slate-600">{e.sin_vencimiento ? 'Sin vencimiento' : fechaAR(e.fecha_vencimiento || e.fecha_expiracion)}</td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${ESTADO[e.estado_vigencia] || ''}`} data-testid={`lb-estado-${e.id}`}>
          {e.estado_vigencia === 'ACTIVA' ? 'Activa' : 'Vencida'}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">{e.creado_por || e.created_by || '—'}<br />{fechaAR(e.fecha_creacion || e.created_at)}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {confirmar ? (
          <>
            <Button size="sm" variant="destructive" onClick={() => onQuitar(e)} data-testid={`lb-quitar-confirmar-${e.id}`}>Quitar</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmar(false)} data-testid={`lb-quitar-cancelar-${e.id}`}>No</Button>
          </>
        ) : (
          <>
            {nueva && <Button size="sm" variant="ghost" onClick={() => onEditar(e)} data-testid={`lb-editar-${e.id}`}><Pencil size={14} /></Button>}
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmar(true)} data-testid={`lb-quitar-${e.id}`}><Trash2 size={14} /></Button>
          </>
        )}
      </td>
    </tr>
  );
};

// Ficha del cliente → sección "Lista blanca" (Parte F7).
export const ClienteListaBlanca = ({ cuit }) => {
  const [data, setData] = useState(null);
  const [dialogo, setDialogo] = useState(null);
  const cargar = useCallback(() => {
    api.get(`/account-lists/lista-blanca/cliente/${cuit}`).then((r) => setData(r.data))
      .catch(() => toast.error('No se pudo cargar la lista blanca del cliente'));
  }, [cuit]);
  useEffect(() => { cargar(); }, [cargar]);

  const quitar = async (e) => {
    try {
      await api.delete(`/account-lists/lista-blanca/${e.id}`);
      toast.success('Exclusión quitada');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo quitar');
    }
  };
  if (!data) return null;

  return (
    <Card className="p-6 bg-white border-slate-200" data-testid="cliente-lista-blanca">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className="text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-900">Lista blanca</h2>
          <Resumen data={data} />
        </div>
        <Button size="sm" onClick={() => setDialogo({})} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lb-agregar">
          <Plus size={14} className="mr-1" /> Agregar exclusión
        </Button>
      </div>
      {data.entradas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[11px] uppercase text-slate-500">
              {['Alcance', 'Motivo', 'Vence', 'Estado', 'Cargado por', ''].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>{data.entradas.map((e) => <Fila key={e.id} e={e} onEditar={(x) => setDialogo({ entrada: x })} onQuitar={quitar} />)}</tbody>
          </table>
        </div>
      )}
      {dialogo && (
        <ListaBlancaDialog cuit={data.cuit} entrada={dialogo.entrada} monitores={data.monitores}
          onClose={() => setDialogo(null)} onDone={() => { setDialogo(null); cargar(); }} />
      )}
    </Card>
  );
};
